import {
  Box,
  Container,
  Paper,
  Tab as TabComponent,
  TabProps,
  Tabs,
  TabsProps,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  fixed?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, fixed, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: '48px', mt: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

interface Tab {
  label: string;
  slug: string;
  component: React.ReactNode;
  hidden?: boolean;
  TabComponentProps?: TabProps;
  icon?: React.ReactElement;
}

interface Props {
  page?: boolean;
  TabsProps?: TabsProps;
  tabs: Tab[];
  fixed?: boolean;
  logo?: React.ReactElement;
  selected?: number;
}

function ViewTabs(props: Props) {
  const { TabsProps, tabs, fixed, logo, page, selected } = props;
  const router = useRouter();
  const [currentTabIndex, setCurrentTabIndex] = React.useState<number>(
    selected || 0
  );

  const handleChange = (event: React.SyntheticEvent, value: number) => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = tabs[value];

    if (!tab) {
      return;
    }

    if (page) {
      router.push(tab.slug);
    } else {
      setCurrentTabIndex(value);
      urlParams.set('tab', tab.slug);

      router.replace({ search: urlParams.toString() });
    }
  };

  useEffect(() => {
    const pathName = router.pathname;
    //Check if route pathname matches tab slug
    let selectedTab = tabs.findIndex((tab) => tab.slug === pathName);

    //if no match, check if tab param matches tab slug
    if (!selectedTab) {
      const urlParams = new URLSearchParams(window.location.search);
      const paramName = urlParams.get('tab');

      if (paramName)
        selectedTab = tabs.findIndex((tab) => tab.slug === paramName);
    }

    if (selectedTab) {
      setCurrentTabIndex(selectedTab);
    }
  }, [tabs]);

  const visibleTabs = tabs.filter((tab) => !tab.hidden);

  return (
    <>
      <Paper
        sx={{
          position: fixed ? 'fixed' : 'inherit',
          zIndex: 2,
          // top: 63,
          width: '100%',
        }}
      >
        <Container maxWidth="xl" sx={{ display: 'flex' }}>
          {logo ? (
            <>
              {logo} <Box sx={{ flexGrow: 1 }} />
            </>
          ) : (
            <>
              <Box /> <Box sx={{ flexGrow: 1 }} />{' '}
            </>
          )}
          <Tabs
            value={currentTabIndex}
            onChange={handleChange}
            variant="scrollable"
            aria-label="View tabs"
            scrollButtons="auto"
            allowScrollButtonsMobile
            {...TabsProps}
          >
            {visibleTabs.map((tab, key) => (
              <TabComponent
                icon={tab.icon ? tab.icon : ''}
                iconPosition="start"
                key={key}
                sx={{
                  minWidth: 125,
                  minHeight: 48,
                  ...tab.TabComponentProps?.sx,
                }}
                label={tab.label}
                {...a11yProps(key)}
              />
            ))}
          </Tabs>
        </Container>
      </Paper>
      {visibleTabs.map((tab, key) => (
        <TabPanel fixed={fixed} key={key} value={currentTabIndex} index={key}>
          {tab.component}
        </TabPanel>
      ))}
    </>
  );
}

export { ViewTabs };
